import { FC, useId } from 'react';
import { buttonClick } from '@belong/analytics';
import useAnalyticsEvents from '@belong/providers/analyticsEvents';
import { COLOURS, ICON_SIZES } from '@belong/themes';
import {
  ICON_NAMES,
  ILinkItem,
  type ITestable,
  STATUS_INDICATOR_STATUSES,
  STATUS_INDICATOR_VARIANT
} from '@belong/types';
import { StatusIndicator } from '../../feedback/StatusIndicator';
import { UnstyledList } from '../../styles/List';
import { Copy } from '../../styles/Typography';
import * as Styles from './LinkList.styles';
import { getAriaProps } from './LinkList.utils';

export const TEST_ID = {
  LIST: 'link-list',
  LINK_ITEM: 'link-item',
  LINK_ICON: 'link-item-icon',
  LINK_LABEL: 'link-item-label',
  LINK_STATUS: 'link-item-status',
  LINK_CAPTION: 'link-item-caption',
  LINK_HREF: 'link-item-href'
};

interface IProps extends ITestable {
  groupName?: string;
  items: ILinkItem[];
}

const LinkList: FC<IProps> = ({ 'data-testid': dataId = TEST_ID.LIST, groupName, items }) => {
  const analyticsEvents = useAnalyticsEvents();
  const linkListId = useId();

  return (
    <UnstyledList data-testid={dataId}>
      {items.map((props, position) => {
        const {
          actionType,
          asUrl,
          caption,
          children,
          ctaIcon,
          href,
          icon,
          id,
          key,
          onClick,
          status,
          target,
          label,
          'data-testid': propTestId,
          ...itemProps
        } = props;
        const iconSize = /(spot|brand)/i.test(icon?.toLowerCase() ?? '') ? ICON_SIZES.medium : ICON_SIZES.small;
        const testId = propTestId ?? (actionType ? `action-${actionType}` : TEST_ID.LINK_HREF);

        const { labelId, statusId, contentId, ...ariaProps } = getAriaProps({
          caption,
          children,
          position,
          status,
          linkListId,
          // The aria labels are in here
          ...itemProps
        });

        return (
          <Styles.ListItem id={id} key={key ?? id ?? label} data-testid={TEST_ID.LINK_ITEM} {...itemProps}>
            <Styles.Link
              onClick={e => {
                analyticsEvents.send({
                  ...buttonClick({
                    label,
                    text: `link-list-item-${(groupName ?? 'unknown-group').replace(/\s/g, '-').toLowerCase()}-${
                      position + 1
                    }`
                  }),
                  listPosition: position + 1,
                  groupName
                });

                onClick?.(e);
              }}
              {...ariaProps}
              asUrl={asUrl}
              href={href}
              target={target}
              data-testid={testId}
            >
              <Styles.LinkGrid>
                {icon ? (
                  <Styles.StyledIcon
                    data-testid={TEST_ID.LINK_ICON}
                    name={icon}
                    size={iconSize}
                    className="label-icon"
                  />
                ) : null}
                <Styles.Label id={labelId} data-testid={TEST_ID.LINK_LABEL}>
                  {label}
                </Styles.Label>
                {status ? (
                  <Styles.StatusWrapper id={statusId}>
                    <StatusIndicator
                      status={STATUS_INDICATOR_STATUSES.INFO}
                      type={STATUS_INDICATOR_VARIANT.NO_STATUS_LIGHT}
                      data-testid={TEST_ID.LINK_STATUS}
                    >
                      {status}
                    </StatusIndicator>
                  </Styles.StatusWrapper>
                ) : null}
                <Styles.RightIcon
                  hasColor={COLOURS.DARK_BLUE}
                  name={ctaIcon ?? ICON_NAMES.ChevronRight}
                  size="small"
                  className="cta-icon"
                />
                <Styles.BodyContent id={contentId}>
                  <>{children}</>
                  {caption && (
                    <Copy variant="small" data-testid={TEST_ID.LINK_CAPTION}>
                      {caption}
                    </Copy>
                  )}
                </Styles.BodyContent>
              </Styles.LinkGrid>
            </Styles.Link>
          </Styles.ListItem>
        );
      })}
    </UnstyledList>
  );
};

LinkList.displayName = 'LinkList';
export default LinkList;
